import React from 'react';
import { Link } from 'react-router-dom';

/* CreditBalance — the LED readout.
   Renders the balance as a glowing digital numeric (mono, tabular, with a
   coloured text-shadow), matching the "scope" treatment in the field-system
   reference. The colour shifts with state:
     · plenty   → led-green   (signal present)
     · low (≤3) → led-amber   (warning)
     · empty    → led-red     (clip / overload) */

interface CreditBalanceProps {
  balance: number;
  showBuyLink?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const sizeStyles = {
  sm: {
    number: 'text-2xl',
    label: 'text-xs',
    buyLink: 'text-xs',
  },
  md: {
    number: 'text-4xl',
    label: 'text-xs',
    buyLink: 'text-xs',
  },
  lg: {
    number: 'text-6xl',
    label: 'text-xs',
    buyLink: 'text-sm',
  },
};

type Tone = { color: string; glow: string; name: string };

function toneFor(balance: number): Tone {
  if (balance <= 0) {
    return {
      color: 'var(--color-led-red)',
      glow: 'rgba(255, 59, 48, 0.5)',
      name: 'empty',
    };
  }
  if (balance <= 3) {
    return {
      color: 'var(--color-led-amber)',
      glow: 'rgba(255, 174, 0, 0.5)',
      name: 'low',
    };
  }
  return {
    color: 'var(--color-led-green)',
    glow: 'rgba(58, 209, 122, 0.45)',
    name: 'ok',
  };
}

export default function CreditBalance({
  balance,
  showBuyLink = false,
  size = 'md',
}: CreditBalanceProps) {
  const styles = sizeStyles[size];
  const tone = toneFor(balance);
  const isLow = tone.name !== 'ok';

  return (
    <div className="flex items-baseline gap-3">
      <span
        className={`font-mono font-normal tracking-[0.04em] leading-none tabular-nums ${styles.number}`}
        style={{
          color: tone.color,
          textShadow: `0 0 12px ${tone.glow}`,
        }}
      >
        {balance}
      </span>
      <span
        className={`font-mono tracking-[0.18em] uppercase text-muted-foreground ${styles.label}`}
      >
        {balance === 1 ? 'credit' : 'credits'}
      </span>

      {showBuyLink && (
        <Link
          to="/credits"
          className={`ml-2 font-mono lowercase tracking-[0.1em] text-primary hover:opacity-80 ${styles.buyLink}`}
        >
          buy more →
        </Link>
      )}

      {isLow && !showBuyLink && (
        <span
          className="ml-1 font-mono text-xs tracking-[0.18em] uppercase"
          style={{
            color: tone.color,
            textShadow: `0 0 6px ${tone.glow}`,
          }}
        >
          ({tone.name})
        </span>
      )}
    </div>
  );
}
